'use client';

import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import Image from 'next/image';
import LeftImg from '../carousel/assets/buttons/left.png';
import LeftHoverImg from '../carousel/assets/buttons/left-hover.png';
import RightImg from '../carousel/assets/buttons/right.png';
import RightHoverImg from '../carousel/assets/buttons/right-hover.png';
import PauseDefaultImg from '../carousel/assets/buttons/pause.png';
import PauseHoverImg from '../carousel/assets/buttons/pause-hover.png';
import PlayDefaultImg from '../carousel/assets/buttons/play.png';
import PlayHoverImg from '../carousel/assets/buttons/play-hover.png';
import DotActiveImg from '../carousel/assets/buttons/dot-active.png';
import DotDefaultImg from '../carousel/assets/buttons/dot.png';
import OurCoachSlide from './our-coach-slide';
import { UserFieldsFragment } from '@/generated/graphql';
import { sectionSpacingCss } from '../section-spacing';
import { config } from '@/config/environment';

const SliderContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 0;
  scroll-snap-type: x mandatory;
  /* hide native scrollbar but keep scrollable */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* WebKit */
  }
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const SlidesRow = styled.div`
  display: flex;
  gap: 16px;
  padding: 8px;
  @media (max-width: 768px) {
    gap: 8px;
    padding: 4px;
  }
`;

const SlideItem = styled.div<{ active?: boolean }>`
  flex: 0 0 100%;
  scroll-snap-align: center;
  display: flex;
  justify-content: center; /* center the slide card within the full-width slot */
  box-sizing: border-box;
  padding: 8px 16px;
  position: relative;
  transition:
    opacity 450ms ease-in-out,
    transform 450ms ease-in-out;
  opacity: ${props => (props.active ? 1 : 0)};
  transform: ${props => (props.active ? 'none' : 'translateY(8px)')};
  pointer-events: ${props => (props.active ? 'auto' : 'none')};
  z-index: ${props => (props.active ? 2 : 1)};
  @media (max-width: 768px) {
    opacity: 1;
    transform: none;
    pointer-events: auto;
  }
`;

const Controls = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
  margin-top: 24px;
  justify-content: center;
`;

const ArrowButton = styled('button')<{ direction?: 'left' | 'right' }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0 10px;
  position: relative;

  .arrow-hover {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }

  &:hover .arrow-default {
    opacity: 0;
  }

  &:hover .arrow-hover {
    opacity: 1;
  }
`;

const Dots = styled('div')`
  display: flex;
  align-items: center;
`;

const PauseButtonRow = styled('div')`
  display: flex;
  justify-content: center;
  margin-top: 10px;
`;

const PauseButton = styled('button')`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const Dot = styled('div')<{ active: boolean }>`
  width: ${props => (props.active ? '24px' : '12px')};
  height: ${props => (props.active ? '24px' : '12px')};
  margin: 0 5px;
  background: ${props =>
    props.active ? `url('${DotActiveImg.src}')` : `url('${DotDefaultImg.src}')`};
  background-size: cover;
  scale: ${props => (props.active ? 1.5 : 1)};
  cursor: pointer;

  &:hover {
    background: url('${DotActiveImg.src}');
    background-size: cover;
    scale: ${props => (props.active ? 1.5 : 2.5)};
  }
`;

const Title = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 50px;
  line-height: 109.3%;
  color: #ffffff;
  text-align: center;
  margin: 0 auto 24px;
  width: 100%;
  max-width: 1100px;
  @media (max-width: 768px) {
    text-align: center;
    margin-bottom: 32px;
  }
`;

const HeaderBox = styled.div`
  ${sectionSpacingCss}
  height: auto;
  min-height: 10vh;
  margin: 0;
  position: relative;
  overflow: hidden;
  color: white;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

export type OurCoachesProps = {
  coaches?: UserFieldsFragment[];
};

export const OurCoaches = ({ coaches }: OurCoachesProps): React.ReactElement => {
  const items = coaches && coaches.length ? coaches : [];
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [pauseHover, setPauseHover] = useState(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    const left = index * el.clientWidth;
    el.scrollTo({ left, behavior: 'smooth' });
  }, [index]);

  useEffect(() => {
    const onResize = () => {
      // ensure slide aligns after resize
      const el = sliderRef.current;
      if (!el) return;
      el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [index]);

  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;

    const onScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        const width = el.clientWidth;
        if (!width) return;
        const newIndex = Math.max(0, Math.min(items.length - 1, Math.round(el.scrollLeft / width)));
        setIndex(prev => (prev === newIndex ? prev : newIndex));
      }, 120);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, [items.length]);

  const prev = () => setIndex(i => (i <= 0 ? items.length - 1 : i - 1));
  const next = () => setIndex(i => (i >= items.length - 1 ? 0 : i + 1));

  // Auto-advance every 15 seconds (unless paused)
  useEffect(() => {
    if (!items.length || items.length <= 1 || isPaused) return;
    const id = setInterval(() => {
      setIndex(i => (i >= items.length - 1 ? 0 : i + 1));
    }, 15000);
    return () => clearInterval(id);
  }, [items.length, isPaused]);

  if (!items.length) {
    return (
      <div>
        <Title>{config.platformMode === 'archive' ? 'Coaches' : 'Meet Our Coaches'}</Title>
        <Typography variant="body2" color="text.secondary">
          No coaches to show yet.
        </Typography>
      </div>
    );
  }

  return (
    <HeaderBox>
      <Title>{config.platformMode === 'archive' ? 'Coaches' : 'Our Coaches'}</Title>
      <SliderContainer ref={sliderRef as any}>
        <SlidesRow>
          {items.map((coach, i) => (
            <SlideItem key={coach?.id ?? `coach-${i}`} active={i === index}>
              <OurCoachSlide coach={coach} />
            </SlideItem>
          ))}
        </SlidesRow>
      </SliderContainer>

      <Controls>
        <ArrowButton onClick={prev} aria-label="Previous">
          <div className="arrow-default">
            <Image width={48} height={48} src={LeftImg} alt="Previous" />
          </div>
          <div className="arrow-hover">
            <Image width={48} height={48} src={LeftHoverImg} alt="Previous" />
          </div>
        </ArrowButton>

        <Dots>
          {items.map((_, i) => (
            <button
              key={`dot-${i}`}
              type="button"
              onClick={() => setIndex(i)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') setIndex(i);
              }}
              aria-label={`Go to slide ${i + 1}`}
              style={{ background: 'none', border: 'none', padding: 0, margin: 0 }}
            >
              <Dot active={i === index} />
            </button>
          ))}
        </Dots>

        <ArrowButton onClick={next} aria-label="Next">
          <div className="arrow-default">
            <Image width={48} height={48} src={RightImg} alt="Next" />
          </div>
          <div className="arrow-hover">
            <Image width={48} height={48} src={RightHoverImg} alt="Next" />
          </div>
        </ArrowButton>
      </Controls>

      <PauseButtonRow>
        <PauseButton
          type="button"
          onClick={() => setIsPaused(p => !p)}
          aria-label={isPaused ? 'Resume auto-rotate' : 'Pause auto-rotate'}
          onMouseEnter={() => setPauseHover(true)}
          onMouseLeave={() => setPauseHover(false)}
        >
          {isPaused ? (
            pauseHover ? (
              <Image width={104} height={33} src={PlayHoverImg} alt="Play" />
            ) : (
              <Image width={104} height={33} src={PlayDefaultImg} alt="Play" />
            )
          ) : pauseHover ? (
            <Image width={104} height={33} src={PauseHoverImg} alt="Pause" />
          ) : (
            <Image width={104} height={33} src={PauseDefaultImg} alt="Pause" />
          )}
        </PauseButton>
      </PauseButtonRow>
    </HeaderBox>
  );
};

export default OurCoaches;
